import { Observable } from 'rxjs';
import { ConnectionState } from './definitions';

export interface ConnectionStateChangeEvent {
  newState: ConnectionState;
  oldState: ConnectionState;
}

/**
 * Communication protocol options
 */
export interface ComProtocolOptions {
  connect: ComProtocolConnectOptions;
  disconnect: ComProtocolDisconnectOptions;
  send: ComProtocolSendOptions;
}

export interface ComProtocolConnectOptions {
  timeout: number;
}

export interface ComProtocolDisconnectOptions {
  timeout: number;
}

export interface ComProtocolSendOptions {
  timeout: number;
}

export interface ComProtocol {
  /**
   * @return connection state (connected or not)
   */
  getConnectionState(): ConnectionState;

  /**
   * @return true if connection to device has been made
   */
  isConnected(): boolean;

  connect(options?: ComProtocolConnectOptions): Observable<any>;

  disconnect(options?: ComProtocolConnectOptions): Observable<any>;

  /**
   * Send data and wait for response
   * @param data
   * @param options
   */
  send(
    data: Uint8Array,
    options?: ComProtocolSendOptions,
  ): Observable<Uint8Array>;

  write(data: Uint8Array): Promise<any>;

  read(): Promise<Uint8Array>;

  /**
   * Message stream. Any message received by the protocol will be notify on this stream
   */
  receiveStream(): Observable<Uint8Array>;

  /**
   * Observe connection state changes
   */
  onConnectionStateChange(): Observable<ConnectionStateChangeEvent>;
}

export namespace ComProtocol {
  export class Errors extends Error {
    public static operationCanceled() {
      return new Errors(
        ErrorCode.OperationCancelled,
        'Communication protocol operation cancelled',
      );
    }

    public static notConnected(info: { protocol: ComProtocol }) {
      return new Errors(
        ErrorCode.ProtocolNotConnected,
        'Communication protocol is not connected',
      );
    }

    public static timeoutError(info: {
      msg?: string;
      protocol: ComProtocol;
      timeout: number;
      startTime: Date;
    }) {
      return new Errors(
        ErrorCode.TimeoutError,
        info.msg || 'Communication protocol timeout',
      );
    }

    public static operationInProgress(msg: string): Errors {
      return new Errors(ErrorCode.OperationInProgress, msg);
    }

    public static unknownError() {
      return new Errors(
        ErrorCode.UnknownError,
        'Unnown communication protocol error',
      );
    }

    // private static fromErrorCode(code: ErrorCode) {
    //     return new ComProtocolError(code, ComProtocolError.errorCodeToMessage(code));
    // }

    // public static errorCodeToMessage(code: ErrorCode): string {
    //     switch (code) {
    //         case ErrorCode.ProtocolNotConnected:
    //             return
    //         case ErrorCode.TimeoutError:
    //             return "request timeout";
    //         default:
    //             return "unknown error"
    //     }
    // }

    private constructor(
      public code: ErrorCode,
      msg: string,
    ) {
      super(msg);
    }
  }

  export enum ErrorCode {
    ProtocolNotConnected = 'ProtocolNotConnected',
    TimeoutError = 'TimeoutError',
    UnknownError = 'UnknownError',
    OperationCancelled = 'OperationCancelled',
    OperationInProgress = 'ComProtocolOperationInProgress',
  }
}
